import React from 'react';
import {useNavigate, useLocation} from "react-router-dom";
import TabList from "@mui/joy/TabList";
import Tab, {tabClasses} from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import Tabs from "@mui/joy/Tabs";

export type NavigationTabPage = {
    tabSlug: string;
    tabTitle: string;
    tabContent: React.ReactNode;
};

export type NavigationTabsProps = {
    tabs: NavigationTabPage[];
};

/** Индекс вкладки по якорю или query: точное совпадение или префикс `slug-`. Берётся самый длинный slug. */
function tabIndexFromSlug(tabs: NavigationTabPage[], raw: string): number {
    const needle = (raw || '').replace('#', '').trim().toLowerCase();
    if (!needle) {
        return -1;
    }
    let best = -1;
    let bestLen = -1;
    tabs.forEach((tab, index) => {
        const slug = tab.tabSlug.toLowerCase();
        const hit = needle === slug || needle.startsWith(`${slug}-`);
        if (hit && slug.length > bestLen) {
            best = index;
            bestLen = slug.length;
        }
    });
    return best;
}

export const NavigationTabs: React.FC<NavigationTabsProps> = React.memo((props) => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        tabs
    } = props;

    const getTabIndexFromLocation = (): number => {
        const fromHash = tabIndexFromSlug(tabs, location.hash);
        if (fromHash >= 0) {
            return fromHash;
        }

        const searchParams = new URLSearchParams(location.search);
        const fromQuery = tabIndexFromSlug(tabs, searchParams.get('tab') || '');
        return fromQuery >= 0 ? fromQuery : 0;
    };

    const [currentTab, setCurrentTab] = React.useState<number>(0);
    
    React.useEffect(() => {
        setCurrentTab(getTabIndexFromLocation());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash, location.search]);

    const handleTabChange = (
        _event: React.SyntheticEvent | null,
        value: number | string | null,
    ) => {
        const nextIndex = typeof value === 'number' ? value : 0;
        setCurrentTab(nextIndex);
        const slug = (tabs[nextIndex] || tabs[0]).tabSlug;
        navigate(`#${slug}`, { replace: false });
    };

    return (
        <Tabs value={currentTab} onChange={handleTabChange} sx={{bgcolor: 'transparent'}}>
            <TabList
                tabFlex={1}
                size="sm"
                sx={{
                    pl: {xs: 0, md: 4},
                    justifyContent: 'left',
                    overflowX: { xs: 'auto', sm: 'visible' },
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    gap: { xs: 0.5, sm: 0 },
                    [`&& .${tabClasses.root}`]: {
                        fontWeight: '600',
                        flex: { xs: '0 0 auto', sm: 'initial' },
                        minWidth: { xs: 140, sm: 'auto' },
                        color: 'text.tertiary',
                        [`&.${tabClasses.selected}`]: {
                            bgcolor: 'transparent',
                            color: 'text.primary',
                            '&::after': {
                                height: '2px',
                                bgcolor: 'primary.500',
                            },
                        },
                    },
                }}
            >
                {tabs.map((tab, idx) => (
                <Tab key={idx} sx={{borderRadius: '6px 6px 0 0', px: { xs: 1, sm: 1.5 }}} indicatorInset value={idx}>
                    {tab.tabTitle}
                </Tab>))}
            </TabList>
            {tabs.map((tab, idx) => (
            <TabPanel key={idx} value={idx}>
                {tab.tabContent}
            </TabPanel>))}
        </Tabs>
    );
});

NavigationTabs.displayName = 'NavigationTabs';